import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { clearAdminImpersonation, getAdminToken, isAdminImpersonating } from "@/lib/adminToken";

interface AdminUser {
  id: number;
  name: string;
  email: string;
  role: "super-admin" | "admin" | "sub-admin" | "editor";
  personal_email?: string;
  gmail_email?: string;
  mail_addresses?: Array<string | { email: string; name?: string }> | string;
}

interface AdminAuthContextType {
  user: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isImpersonating: boolean;
  hasRole: (roles: string[]) => boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(isAdminImpersonating);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<string | null>(null);

  // Validate token on mount
  useEffect(() => {
    const validate = async () => {
      const storedToken = getAdminToken();
      if (!storedToken) {
        setSessionExpiresAt(null);
        setIsLoading(false);
        return;
      }

      try {
        const res = await fetch("/api/admin/auth", {
          headers: { Authorization: `Bearer ${storedToken}` },
        });
        const data = await res.json();

        if (res.ok && data.user) {
          setUser(data.user);
          setSessionExpiresAt(data.expiresAt || null);
        } else {
          setSessionExpiresAt(null);
          if (isAdminImpersonating()) {
            sessionStorage.removeItem("adminImpersonationToken");
          } else {
            localStorage.removeItem("adminToken");
            localStorage.removeItem("adminUser");
          }
        }
      } catch (err) {
        console.error("Token validation error:", err);
        setSessionExpiresAt(null);
        if (isAdminImpersonating()) {
          sessionStorage.removeItem("adminImpersonationToken");
        } else {
          localStorage.removeItem("adminToken");
          localStorage.removeItem("adminUser");
        }
      } finally {
        setIsLoading(false);
      }
    };

    validate();
  }, []);

  const hasRole = useCallback(
    (roles: string[]) => {
      if (!user) return false;
      return roles.includes(user.role);
    },
    [user]
  );

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        const res = await fetch("/api/admin/auth", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        });
        const data = await res.json();

        if (res.ok && data.success) {
          if (isAdminImpersonating()) {
            sessionStorage.setItem("adminImpersonationToken", data.token);
          } else {
            localStorage.setItem("adminToken", data.token);
            localStorage.setItem("adminUser", JSON.stringify(data.user));
          }
          setUser(data.user);
          setSessionExpiresAt(data.expiresAt || null);
          return { success: true };
        }
        return { success: false, error: data.error || "Login failed" };
      } catch (err) {
        console.error("Login error:", err);
        return { success: false, error: "Network error. Please try again." };
      }
    },
    [isImpersonating]
  );

  const logout = useCallback(async () => {
    const impersonating = isAdminImpersonating();
    const storedToken = getAdminToken();
    if (storedToken) {
      try {
        await fetch("/api/admin/auth", {
          method: "DELETE",
          headers: { Authorization: `Bearer ${storedToken}` },
        });
      } catch (err) {
        console.error("Logout API error:", err);
      }
    }
    if (impersonating) {
      clearAdminImpersonation();
      setIsImpersonating(false);
    } else {
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminUser");
    }
    setUser(null);
    setSessionExpiresAt(null);
    window.location.href = impersonating ? "/admin" : "/admin/login";
  }, []);

  useEffect(() => {
    if (!user || !sessionExpiresAt) return;
    const expirationTime = Date.parse(sessionExpiresAt);
    if (!Number.isFinite(expirationTime)) return;

    const timer = window.setTimeout(() => {
      void logout();
    }, Math.max(0, expirationTime - Date.now()));
    return () => window.clearTimeout(timer);
  }, [user, sessionExpiresAt, logout]);

  return (
    <AdminAuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        isImpersonating,
        hasRole,
        login,
        logout,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error("useAdminAuth must be used within AdminAuthProvider");
  }
  return context;
}
